import { useMemo, useState } from "react";
import { applyPlaceholderPreview, mailPreviewValues, mailTokenLabel } from "@/lib/templatePlaceholders";
import { isBlankMailText, renderFinishedMail } from "@/lib/emailPreview";
import { useMailPlacePreview } from "@/lib/useMailPlacePreview";
import { listMailRules, rulePhrase, valuesForPreviewRules, type MailRule } from "@/lib/templateConditions";

function ruleId(rule: MailRule, index: number): string {
  return `${index}|${rule.key}|${rule.op}|${rule.a}|${rule.b}`;
}

export function FinishedMailPreview({
  layoutHtml,
  body,
  subject,
  extra = "",
  className = "h-[32rem]",
  includeSignature = true,
  samples,
  layoutId,
}: {
  layoutHtml: string;
  body: string;
  subject?: string;
  extra?: string;
  className?: string;
  includeSignature?: boolean;
  samples?: Record<string, string>;
  layoutId?: string;
}) {
  const place = useMailPlacePreview();
  const rules = useMemo(() => listMailRules(body), [body]);
  const [turnedOff, setTurnedOff] = useState<Record<string, boolean>>({});
  const sampleOverrides = useMemo(() => {
    const base = mailPreviewValues({ ...place.samples, ...samples });
    const enabled = rules.map((rule, index) => !turnedOff[ruleId(rule, index)]);
    return valuesForPreviewRules(base, rules, enabled);
  }, [place.samples, rules, samples, turnedOff]);
  const doc = useMemo(
    () =>
      layoutHtml.trim()
        ? renderFinishedMail(layoutHtml, body, extra, {
            includeSignature,
            sampleOverrides,
            layoutId,
            ...(place.known ? { logoHtml: place.logoHtml, signatureHtml: place.signatureHtml } : {}),
          })
        : "",
    [body, extra, includeSignature, layoutHtml, layoutId, place.known, place.logoHtml, place.signatureHtml, sampleOverrides],
  );
  const subjectPreview = subject ? applyPlaceholderPreview(subject, sampleOverrides) : "";
  const bodyEmpty = isBlankMailText(body);

  if (bodyEmpty) {
    return (
      <div className="space-y-2">
        <p className="text-xs font-medium text-label-secondary">Fertige Mail</p>
        {subjectPreview ? (
          <p className="text-sm text-label">
            <span className="text-label-secondary">Betreff: </span>
            {subjectPreview}
          </p>
        ) : null}
        <p className="text-sm italic text-label-tertiary">Die Vorschau ist leer, solange der Text leer ist.</p>
      </div>
    );
  }

  if (!layoutHtml.trim()) {
    return <p className="text-sm text-label-tertiary">Das HTML-Design wird geladen…</p>;
  }

  return (
    <div className="space-y-2">
      <p className="text-xs font-medium text-label-secondary">Fertige Mail</p>
      {subjectPreview ? (
        <p className="text-sm text-label">
          <span className="text-label-secondary">Betreff: </span>
          {subjectPreview}
        </p>
      ) : null}
      {rules.length > 0 ? (
        <div className="flex flex-wrap gap-x-4 gap-y-2">
          {rules.map((rule, index) => {
            const id = ruleId(rule, index);
            return (
              <label key={id} className="flex items-center gap-2 text-xs text-label-secondary">
                <input
                  type="checkbox"
                  checked={!turnedOff[id]}
                  onChange={(event) => setTurnedOff((current) => ({ ...current, [id]: !event.target.checked }))}
                />
                {rulePhrase(rule, mailTokenLabel(rule.key))}
              </label>
            );
          })}
        </div>
      ) : null}
      <iframe
        title="Vorschau der fertigen E-Mail"
        sandbox=""
        srcDoc={doc}
        className={`${className} w-full rounded-xl border border-separator bg-white`}
      />
    </div>
  );
}
